import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { IconLock } from "@tabler/icons-react";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ListaControlesSkeleton,
  RegionCargando,
  Skeleton,
  Stack,
  Text,
} from "@/components/ui";
import { permisosCatalogoQueryOptions } from "../hooks/use-roles-admin";
import { useAsignarPermisos } from "../hooks/use-roles-mutations";
import type { PermisoCatalogo, RolAdmin } from "../services/roles-service";

interface Props {
  rol: RolAdmin;
  open: boolean;
  onClose: () => void;
}

/**
 * Editor de permisos de un rol · checkboxes agrupados por área. Para roles del
 * SISTEMA es de solo lectura (permisos fijos); para personalizados permite el
 * reemplazo total (set-replace) y guarda con un solo PUT.
 */
export function RolPermisosDialog({ rol, open, onClose }: Props) {
  const { data: catalogo, isLoading } = useQuery(permisosCatalogoQueryOptions);
  const [seleccion, setSeleccion] = useState<Set<string>>(() => new Set(rol.permisos));
  const asignar = useAsignarPermisos({ onAsignado: onClose });

  const soloLectura = rol.esSistema;

  const grupos = useMemo(() => agrupar(catalogo ?? []), [catalogo]);

  const toggle = (codigo: string, on: boolean) =>
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (on) next.add(codigo);
      else next.delete(codigo);
      return next;
    });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Permisos · {rol.nombre}{" "}
            <Badge size="sm" color={rol.esSistema ? "info" : "slate"}>
              {rol.esSistema ? "Sistema" : "Personalizado"}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {soloLectura ? (
          <Alert variant="info" icon={<IconLock size={16} />}>
            Los permisos de los roles del sistema son fijos (reflejan el acceso base del portal) y no se editan.
          </Alert>
        ) : (
          <Text size="sm" c="dimmed">
            Marca los permisos que este rol otorga. El cambio aplica al instante en el backend; en la sesión de
            cada usuario, tras su próximo inicio de sesión.
          </Text>
        )}

        {isLoading ? (
          <RegionCargando etiqueta="Cargando permisos…" className="flex flex-col gap-6">
            {[4, 3].map((n, i) => (
              <div key={i} className="flex flex-col gap-2">
                <Skeleton height={10} width={90} aria-hidden="true" />
                <ListaControlesSkeleton filas={n} control="checkbox" conDescripcion anunciar={false} />
              </div>
            ))}
          </RegionCargando>
        ) : (
          <div className="max-h-[55vh] overflow-y-auto pr-1">
            <Stack gap="lg">
              {grupos.map(([grupo, permisos]) => (
                <Stack key={grupo} gap={8}>
                  <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                    {grupo}
                  </Text>
                  <Stack gap={6}>
                    {permisos.map((p) => (
                      <Checkbox
                        key={p.codigo}
                        label={
                          p.pendienteCablear ? (
                            <span>
                              {p.descripcion}{" "}
                              <Badge size="xs" color="advertencia" title="No otorga acceso hasta cablearse en el código">
                                pendiente de cablear
                              </Badge>
                            </span>
                          ) : (
                            p.descripcion
                          )
                        }
                        description={p.codigo}
                        checked={seleccion.has(p.codigo)}
                        onCheckedChange={(v) => toggle(p.codigo, Boolean(v))}
                        disabled={soloLectura}
                      />
                    ))}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </div>
        )}

        <DialogFooter>
          <Button variant="default" onClick={onClose}>
            {soloLectura ? "Cerrar" : "Cancelar"}
          </Button>
          {!soloLectura ? (
            <Button loading={asignar.isPending} onClick={() => asignar.mutate({ id: rol.id, permisos: [...seleccion] })}>
              Guardar permisos
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Agrupa el catálogo por `grupo`, conservando el orden de aparición. */
function agrupar(catalogo: PermisoCatalogo[]): Array<[string, PermisoCatalogo[]]> {
  const mapa = new Map<string, PermisoCatalogo[]>();
  for (const p of catalogo) {
    const lista = mapa.get(p.grupo) ?? [];
    lista.push(p);
    mapa.set(p.grupo, lista);
  }
  return [...mapa.entries()];
}
